import React, { useState } from 'react';
import { 
  Paper, 
  InputBase, 
  IconButton, 
  FormControl,
  FormHelperText,
  Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VoiceInputButton from './VoiceInputButton.tsx';
import { NewTodo } from '../../types/todos';
import { enhanceTranscription } from '../../services/ai/transcriptionService.ts';

interface TodoFormProps {
  onAddTodo: (todo: NewTodo) => Promise<void>;
}

const TodoForm: React.FC<TodoFormProps> = ({ onAddTodo }) => {
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Todo cannot be empty');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      await onAddTodo({ title });
      setTitle('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add todo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoiceInput = async (text: string) => {
    try {
      setIsEnhancing(true);
      // Use browser language or get the user's configured language
      const userLanguage = navigator.language || 'en-US';
      
      // Enhance the text
      const enhancedText = await enhanceTranscription(text, userLanguage);
      
      setTitle(enhancedText);
      setError(null);
    } catch (err) {
      console.error('Error processing voice input:', err);
      // Still set the raw text even if enhancement fails
      setTitle(text);
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <Paper 
      component="form" 
      onSubmit={handleSubmit}
      sx={{ 
        p: '2px 4px', 
        display: 'flex', 
        alignItems: 'center',
        mb: 2,
        boxShadow: 2
      }}
    >
      <FormControl fullWidth error={!!error}>
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Add a new todo..."
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (error) setError(null);
          }}
          disabled={isSubmitting || isEnhancing}
          fullWidth
          required
        />
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <VoiceInputButton onTextCaptured={handleVoiceInput} />
        
        <IconButton 
          type="submit" 
          color="primary" 
          sx={{ p: '10px' }} 
          aria-label="add todo"
          disabled={isSubmitting || isEnhancing}
        >
          <AddIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default TodoForm;